import { FC, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box, Button, Dialog, FormControlLabel, IconButton, Link, MenuItem, Radio, RadioGroup, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import { Add as AddIcon, Close } from '@mui/icons-material';
import { appointmentService, SchedulePreferences } from '../../services/appointmentService';
import { useIsOffline } from '../../offline';
import { useRBAC } from '../../auth/useRBAC';
import { calendarSettingsControls } from '../../auth/pageControls';
import { serviceCatalogService } from '../../services/serviceCatalogService';
import { AddTypeDialog, SectionLabel, Segmented } from './scheduleFormParts';

const EMPTY: SchedulePreferences = { slotMinutes: null, dayStartHour: null, dayEndHour: null, unassignedColumn: null };
const KEY = ['schedule-preferences'];

/**
 * One person's Schedule settings, saved on the server so they follow the person to any computer.
 * Offline they read as the defaults and changes are not saved (the panel says so).
 */
export const useSchedulePreferences = () => {
  const isOffline = useIsOffline();
  const queryClient = useQueryClient();
  const { data } = useQuery<SchedulePreferences>(KEY, appointmentService.getSchedulePreferences, {
    enabled: !isOffline,
    staleTime: Infinity,
  });
  const mutation = useMutation((patch: Partial<SchedulePreferences>) => appointmentService.saveSchedulePreferences(patch), {
    onMutate: (patch) => {
      queryClient.setQueryData<SchedulePreferences>(KEY, (old) => ({ ...EMPTY, ...old, ...patch }));
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(KEY, saved);
    },
    onError: () => {
      queryClient.invalidateQueries(KEY);
    },
  });
  const prefs: SchedulePreferences = { ...EMPTY, ...(data && !Array.isArray(data) ? data : {}) };
  return {
    prefs,
    save: (patch: Partial<SchedulePreferences>) => {
      if (!isOffline) mutation.mutate(patch);
    },
  };
};

const hourLabel = (h: number) => (h === 0 || h === 24 ? '12:00 AM' : h === 12 ? '12:00 PM' : h < 12 ? `${h}:00 AM` : `${h - 12}:00 PM`);

/**
 * One appointment type's colour. Editable rows save the catalog row's colour (the Schedule's type
 * colours live here now, not in Service Pricing); a drag through the picker saves once it settles.
 */
const TypeColorRow: FC<{ type: { id?: string; name: string; color: string }; editable: boolean }> = ({ type, editable }) => {
  const queryClient = useQueryClient();
  const [color, setColor] = useState(type.color);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => setColor(type.color), [type.color]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const save = useMutation((next: string) => serviceCatalogService.update(type.id!, { color: next }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['service-catalog']);
    },
    onError: () => setColor(type.color),
  });
  const pick = (next: string) => {
    setColor(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save.mutate(next), 600);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, border: '1px solid #E5E7EB', borderRadius: 2, minWidth: 0 }}>
      {editable ? (
        <Box
          component="input"
          type="color"
          value={color}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => pick(e.target.value)}
          aria-label={`Color for ${type.name}`}
          sx={{ flex: 'none', width: 28, height: 28, p: 0, border: '1px solid #D1D5DB', borderRadius: 1.5, bgcolor: 'background.paper', cursor: 'pointer' }}
        />
      ) : (
        <Box sx={{ flex: 'none', width: 22, height: 22, borderRadius: 1.5, bgcolor: color, border: '1px solid rgba(0,0,0,0.08)' }} />
      )}
      <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{type.name}</Typography>
    </Box>
  );
};

const GroupHeading: FC<{ title: string; note: string }> = ({ title, note }) => (
  <Box sx={{ pb: 1, borderBottom: '1px solid #F0F1F3' }}>
    <Typography component="h3" sx={{ m: 0, fontSize: '0.95rem', fontWeight: 700, color: '#3B2E1A' }}>{title}</Typography>
    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{note}</Typography>
  </Box>
);

/** Calendar settings (Schedule canvas board "Calendar settings"). */
const CalendarSettings: FC<{
  open: boolean;
  onClose: () => void;
  prefs: SchedulePreferences;
  onSave: (patch: Partial<SchedulePreferences>) => void;
  /** Appointment types; `id` is set for the clinic's catalog rows, the ones whose colour can change. */
  types: Array<{ id?: string; name: string; color: string }>;
}> = ({ open, onClose, prefs, onSave, types }) => {
  const rbac = useRBAC();
  const can = calendarSettingsControls(rbac);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isOffline = useIsOffline();
  const [addingType, setAddingType] = useState(false);
  const start = prefs.dayStartHour ?? 6;
  const end = prefs.dayEndHour ?? 24;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{ 'aria-label': 'Calendar settings', sx: { width: 480, maxWidth: '100%', maxHeight: { sm: 'calc(100% - 64px)' }, display: 'flex', flexDirection: 'column', borderRadius: { sm: 3 } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2, borderBottom: '1px solid #F0F1F3' }}>
        <Typography component="h2" sx={{ flex: 1, m: 0, fontSize: '1.1rem', fontWeight: 700, color: '#3B2E1A' }}>Calendar settings</Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ width: 36, height: 36 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.75 }}>
        {isOffline && (
          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>Settings need a connection. The defaults show until you are back online.</Typography>
        )}

        <GroupHeading title="User settings" note="Yours only, on any computer you sign in on." />

        <Box>
          <SectionLabel>Unassigned column</SectionLabel>
          <RadioGroup
            aria-label="Unassigned column"
            value={prefs.unassignedColumn ?? 'auto'}
            onChange={(e) => onSave({ unassignedColumn: e.target.value as SchedulePreferences['unassignedColumn'] })}
          >
            {[
              ['auto', 'Only when something is unassigned'],
              ['always', 'Always'],
              ['never', 'Never'],
            ].map(([value, label]) => (
              <FormControlLabel key={value} value={value} control={<Radio size="small" disabled={isOffline} />} label={label} sx={{ '& .MuiTypography-root': { fontSize: '0.88rem' } }} />
            ))}
          </RadioGroup>
        </Box>

        <Box>
          <SectionLabel>Time slots</SectionLabel>
          <Segmented
            label="Time slots"
            value={String(prefs.slotMinutes ?? 60)}
            onChange={(v) => onSave({ slotMinutes: Number(v) as 15 | 30 | 60 })}
            options={['15', '30', '60'].map((m) => ({ value: m, label: `${m} min`, disabled: isOffline }))}
          />
          <Typography sx={{ mt: 0.75, fontSize: '0.78rem', color: 'text.secondary' }}>Kept the next time you open the schedule.</Typography>
        </Box>

        <Box>
          <SectionLabel>Hours shown</SectionLabel>
          <Box sx={{ display: 'flex', gap: 1.25 }}>
            <TextField
              select
              size="small"
              label="From"
              value={start}
              disabled={isOffline}
              onChange={(e) => onSave({ dayStartHour: Number(e.target.value) })}
              sx={{ flex: 1 }}
            >
              {Array.from({ length: end }, (_, h) => h).map((h) => (
                <MenuItem key={h} value={h}>{hourLabel(h)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="To"
              value={end}
              disabled={isOffline}
              onChange={(e) => onSave({ dayEndHour: Number(e.target.value) })}
              sx={{ flex: 1 }}
            >
              {Array.from({ length: 24 - start }, (_, i) => start + i + 1).map((h) => (
                <MenuItem key={h} value={h}>{hourLabel(h)}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Typography sx={{ mt: 0.75, fontSize: '0.78rem', color: 'text.secondary' }}>An appointment outside these hours still shows; the day stretches to fit it.</Typography>
        </Box>

        <GroupHeading title="Clinic settings" note="The same for everyone at the clinic." />

        <Box>
          <SectionLabel>Appointment types</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
            {types.map((t) => (
              <TypeColorRow key={t.name} type={t} editable={can.manageTypes && !!t.id && !isOffline} />
            ))}
          </Box>
          {can.manageTypes && !isOffline && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAddingType(true)}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
            >
              Add appointment type
            </Button>
          )}
          <Typography sx={{ mt: 0.75, fontSize: '0.78rem', color: 'text.secondary' }}>
            {can.manageTypes ? 'Any color, not just presets. ' : 'Owners and admins add types and choose their colors. '}
            Rename types or set their prices in{' '}
            <Link component={RouterLink} to="/settings?tab=service-pricing" onClick={onClose}>Settings, Service Pricing</Link>.
          </Typography>
          <AddTypeDialog open={addingType} onClose={() => setAddingType(false)} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, py: 1.75, borderTop: '1px solid #F0F1F3' }}>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', height: 40 }}>
          Done
        </Button>
      </Box>
    </Dialog>
  );
};

export default CalendarSettings;
